import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import api from "../api";
import { loadRazorpay } from "../utils/loadRazorpay";
import { MindConnectMark, MindConnectWordmark, MindConnectTagline } from "../components/ui/MindConnectLogo";

const CYCLE_LABELS = { monthly: "Monthly", quarterly: "Quarterly", half_yearly: "Half-yearly", yearly: "Yearly" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};
const addDays = (iso, days) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + (days || 0));
  return d.toLocaleDateString("en-CA");
};

// Rendered OUTSIDE the normal DashboardLayout shell - the rest of the
// app's nav/data calls would just 402 the same way the hard lock does, so
// there's nothing for a Sidebar/DashboardLayout to usefully show here.
// Reached via api.js's response interceptor whenever any call 402s with
// error: "subscription_expired". Only /organizations/subscription* calls
// are allowed through while locked, which is all this page uses.
export default function SubscriptionLocked() {
  const [subscription, setSubscription] = useState(null);
  const [cycleOptions, setCycleOptions] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState("");
  const [aiOption, setAiOption] = useState("addon");
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  let role = null;
  let userName = null;
  try {
    const authData = JSON.parse(localStorage.getItem("auth") || "{}");
    if (authData.token) {
      role = (jwtDecode(authData.token)?.sub?.role || "").toLowerCase();
      userName = authData.user_name || null;
    }
  } catch {
    // no valid session - fine, just means no role-gated actions render
  }
  const isAdmin = role === "admin";

  useEffect(() => {
    api
      .fetchSubscription()
      .then((res) => {
        const data = res.data || {};
        setSubscription(data);
        if (data.has_subscription && isAdmin) {
          return api.fetchSubscriptionCycleOptions().then((r) => {
            const opts = Array.isArray(r.data) ? r.data : [];
            setCycleOptions(opts);
            const current = opts.find((o) => o.is_current) || opts.find((o) => o.billing_cycle === data.billing_cycle) || opts[0];
            if (current) setSelectedCycle(current.billing_cycle);
          });
        }
        return null;
      })
      .catch(() => setError("Could not load subscription status."))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  const logout = () => {
    ["auth", "chatState", "patient_type", "username", "token"].forEach((k) => localStorage.removeItem(k));
    window.location.href = "/";
  };

  const isTrial = !!subscription?.is_trial;
  const hasSubscription = !!subscription?.has_subscription;
  const selected = cycleOptions.find((o) => o.billing_cycle === selectedCycle);
  const wantsAi = isTrial ? aiOption === "addon" : !!subscription?.ai_addon_enabled;
  const amount = selected
    ? Number(selected.base_price || 0) + (wantsAi ? Number(selected.ai_addon_price || 0) : 0)
    : Number(subscription?.current_price_per_cycle || 0);
  const endedOn = isTrial ? subscription?.trial_end : subscription?.end_date;
  const pausedOn = endedOn ? addDays(endedOn, subscription?.grace_days) : null;

  const handlePayNow = async () => {
    setPaying(true);
    setError(null);
    try {
      const orderRes = await api.createSubscriptionOrder({
        ...(selectedCycle ? { billing_cycle: selectedCycle } : {}),
        ...(isTrial ? { ai_option: aiOption } : {}),
      });
      const { payment_id, gateway_order_id, amount: payAmount, key_id } = orderRes.data;

      const ready = await loadRazorpay();
      if (!ready || !key_id) {
        setError("Payment gateway is not available right now. Please try again shortly.");
        setPaying(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: key_id,
        order_id: gateway_order_id,
        amount: Math.round(payAmount * 100),
        currency: "INR",
        name: subscription?.org_name || "Subscription Renewal",
        description: "MindConnect subscription renewal",
        prefill: { email: subscription?.org_email || undefined },
        handler: async (response) => {
          try {
            await api.verifySubscriptionPayment({
              payment_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            window.location.href = "/dashboard";
          } catch {
            setError("Payment succeeded but could not be verified. Please contact support.");
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      });
      rzp.open();
    } catch (e) {
      setError(e?.response?.data?.error || "Could not start payment. Please try again.");
      setPaying(false);
    }
  };

  const title = !hasSubscription ? "No Active Subscription" : isTrial ? "Your Free Trial Has Ended" : "Subscription Expired";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary-50 to-accent-50 px-4 py-10">
      {/* Same brand lockup as the login page (AuthLayout.js). */}
      <div className="flex items-center gap-3 mb-6">
        <MindConnectMark className="w-10 h-10" />
        <div className="leading-tight">
          <MindConnectWordmark className="text-3xl" />
          <MindConnectTagline className="text-xs" />
        </div>
      </div>
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        {loading ? (
          <p className="text-secondary-500 text-center">Loading...</p>
        ) : (
          <>
            {subscription?.org_name && (
              <p className="text-xs font-semibold uppercase tracking-wide text-secondary-400 text-center">{subscription.org_name}</p>
            )}
            <h1 className="text-xl font-bold text-secondary-900 mt-1 mb-2 text-center">{title}</h1>

            <p className="text-secondary-600 text-center mb-6">
              {!hasSubscription ? (
                <>Your organisation doesn't have a subscription set up yet, so access is paused.</>
              ) : (
                <>
                  {isTrial ? "Your free trial ended on " : "Your subscription ended on "}
                  <span className="font-semibold">{fmtDate(endedOn) || "an earlier date"}</span>
                  {pausedOn && <> and access was paused on <span className="font-semibold">{fmtDate(pausedOn)}</span></>}.
                  {" "}Your data is safe; renew to pick up where you left off.
                </>
              )}
            </p>

            {hasSubscription && !isTrial && (
              <dl className="grid grid-cols-2 gap-3 rounded-xl bg-secondary-50 p-4 text-sm mb-6">
                <div>
                  <dt className="text-secondary-500">Current plan</dt>
                  <dd className="font-medium text-secondary-900">{CYCLE_LABELS[subscription.billing_cycle] || subscription.billing_cycle}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Seats</dt>
                  <dd className="font-medium text-secondary-900">{subscription.seat_limit ?? "Unlimited"}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Practice management</dt>
                  <dd className="font-medium text-secondary-900">{inr(subscription.base_price_per_cycle)}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">AI features</dt>
                  <dd className="font-medium text-secondary-900">
                    {subscription.ai_addon_enabled
                      ? inr(subscription.ai_addon_price_per_cycle)
                      : subscription.ai_billing_mode === "per_session"
                        ? `Pay per session (${inr(subscription.ai_price_per_session)} each)`
                        : "Not included"}
                  </dd>
                </div>
              </dl>
            )}

            {!hasSubscription ? (
              <p className="text-secondary-600 text-sm text-center">
                Contact {subscription?.support_name || "MindConnect support"} at{" "}
                <a className="font-semibold text-primary-600 underline" href={`mailto:${subscription?.support_email}`}>
                  {subscription?.support_email}
                </a>{" "}
                to choose a plan.
              </p>
            ) : isAdmin ? (
              <div className="space-y-4">
                {cycleOptions.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1" htmlFor="renew-cycle">Billing cycle</label>
                    <select
                      id="renew-cycle"
                      value={selectedCycle}
                      onChange={(e) => setSelectedCycle(e.target.value)}
                      className="w-full rounded-lg border border-secondary-300 p-2 text-sm"
                    >
                      {cycleOptions.map((o) => (
                        <option key={o.billing_cycle} value={o.billing_cycle}>
                          {CYCLE_LABELS[o.billing_cycle] || o.billing_cycle} ({o.cycle_days} days){o.is_current ? " - current" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {isTrial && (
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium text-secondary-700 mb-1">How would you like to pay for AI features?</legend>
                    <label className="flex items-start gap-2 rounded-lg border border-secondary-200 p-3 text-sm cursor-pointer">
                      <input type="radio" name="ai_option" className="mt-1" checked={aiOption === "addon"} onChange={() => setAiOption("addon")} />
                      <span>
                        <span className="font-medium text-secondary-900">AI add-on</span>{" "}
                        {selected?.ai_addon_price != null && <span className="text-secondary-500">({inr(selected.ai_addon_price)} per cycle)</span>}
                        <span className="block text-secondary-500">A fixed daily allowance of AI sessions.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 rounded-lg border border-secondary-200 p-3 text-sm cursor-pointer">
                      <input type="radio" name="ai_option" className="mt-1" checked={aiOption === "per_session"} onChange={() => setAiOption("per_session")} />
                      <span>
                        <span className="font-medium text-secondary-900">Pay per AI session</span>
                        <span className="block text-secondary-500">Only pay for the AI sessions you use; deducted from your payouts.</span>
                      </span>
                    </label>
                  </fieldset>
                )}

                <div className="flex items-baseline justify-between border-t border-secondary-100 pt-4">
                  <span className="text-sm text-secondary-600">Amount due</span>
                  <span className="text-2xl font-bold text-secondary-900">{inr(amount)}</span>
                </div>
                <button
                  onClick={handlePayNow}
                  disabled={paying || amount <= 0}
                  className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors"
                >
                  {paying ? "Processing..." : "Pay Now"}
                </button>
              </div>
            ) : (
              <p className="text-secondary-500 text-sm text-center">
                Ask your organisation's admin to renew the subscription.
              </p>
            )}

            {error && <p className="mt-4 text-sm text-red-600 text-center">{error}</p>}

            <div className="mt-6 border-t border-secondary-100 pt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-secondary-500">
              <span>
                Need help?{" "}
                <a className="text-primary-600 underline" href={`mailto:${subscription?.support_email || "mk@nllabs.in"}`}>
                  {subscription?.support_email || "mk@nllabs.in"}
                </a>
              </span>
              <span>
                {userName && <>Signed in as {userName} · </>}
                <button type="button" onClick={logout} className="text-primary-600 underline">Log out</button>
              </span>
            </div>
          </>
        )}
      </div>
      <p className="mt-6 text-xs text-secondary-500 text-center">
        © {new Date().getFullYear()} {subscription?.support_name || "NeuralLoom Labs Private Limited"}
      </p>
    </div>
  );
}
